import { proxy } from 'valtio';
import {
  BasicInfoDataTypes,
  EnrolmentSettingsDataTypes,
} from '../components/QuickReferralStepForms/types';
import { WorkSchedule, CourseSegment } from '@duke-hq/shared-constants/enrolments';
import { djs } from '@duke-hq/djs';
import { Decimal } from 'decimal.js';

export function unknownOrDecimalToNumber(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return Number(value);
  }

  if (value instanceof Decimal) {
    return Number(value.toFixed(2));
  }

  return Number(value);
}

export const quickReferralModalState = proxy({
  basicInfoData: {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    date_of_birth: null as Date | null,
    universal_student_identifier: '',
  },
  courseData: {
    id: '',
    title: '',
    identifier: '' as string | null,
    specialisation: '' as string | null,
    kind: '',
    description: '' as string | null,
    price_aud: 0,
    accredited: false,
  },
  enrolmentSettingsData: {
    start_at_date: djs().format('YYYY-MM-DD'),
    external_id: '',
    enrolment_contract_type: '',
    contract_category: '',
    work_schedule: WorkSchedule.FULL_TIME,
    owned_by: '',
    course_segment: CourseSegment.FULL_COURSE,
    selected_consultant: {
      consultant_id: '',
      consultant_name: '',
    },
    selected_site: {
      site_id: '',
      site_name: '',
      site_state: '',
    },
  },
  isModalOpen: false,
  existingFields: null as {
    email: boolean;
    firstName: boolean;
    lastName: boolean;
    phoneNumber: boolean;
    dateOfBirth: boolean;
    universalStudentIdentifier: boolean;
    usiVerified: boolean;
  } | null,
  existingStudentId: null as string | null,
  hasActiveAccreditedCourse: false,
  usiConflict: false,
  setSite: ({
    site_id,
    site_name,
    site_state,
  }: {
    site_id: string;
    site_name: string;
    site_state: string;
  }) => {
    quickReferralModalState.enrolmentSettingsData.selected_site = {
      site_id,
      site_name,
      site_state,
    };
  },
  setConsultant: ({
    consultant_id,
    consultant_name,
  }: {
    consultant_id: string;
    consultant_name: string;
  }) => {
    quickReferralModalState.enrolmentSettingsData.selected_consultant = {
      consultant_id,
      consultant_name,
    };
  },
  setBasicInfoData: ({
    first_name,
    last_name,
    email,
    phone_number,
    date_of_birth,
    universal_student_identifier,
  }: BasicInfoDataTypes) => {
    quickReferralModalState.basicInfoData = {
      first_name,
      last_name,
      email,
      phone_number,
      date_of_birth: date_of_birth ? new Date(date_of_birth) : (null as Date | null), //TODO - this will be open to date conversion issues - should use a string instead
      universal_student_identifier,
    };
  },
  setCourseData: (course: {
    id: string;
    title: string;
    identifier: string | null;
    specialisation: string | null;
    kind: string;
    description: string | null;
    price_aud: number | Decimal;
    accredited: boolean;
  }) => {
    quickReferralModalState.courseData = {
      ...course,
      price_aud: unknownOrDecimalToNumber(course.price_aud),
    };
  },
  setEnrolmentSettingsData: ({
    start_at_date,
    external_id,
    enrolment_contract_type,
    contract_category,
    work_schedule,
    owned_by,
    course_segment,
    selected_site,
    selected_consultant,
  }: EnrolmentSettingsDataTypes) => {
    quickReferralModalState.enrolmentSettingsData = {
      start_at_date: djs(start_at_date).format('YYYY-MM-DD'),
      external_id,
      enrolment_contract_type,
      contract_category,
      work_schedule,
      owned_by,
      course_segment,
      selected_site,
      selected_consultant,
    };
  },
  openModal: () => {
    quickReferralModalState.resetStates();
    quickReferralModalState.isModalOpen = true;
  },
  closeModal: () => {
    quickReferralModalState.isModalOpen = false;
    quickReferralModalState.resetStates();
  },
  resetStates: () => {
    quickReferralModalState.basicInfoData = {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      date_of_birth: null,
      universal_student_identifier: '',
    };
    quickReferralModalState.courseData = {
      id: '',
      title: '',
      identifier: '',
      specialisation: '',
      kind: '',
      description: '',
      price_aud: 0,
      accredited: false,
    };
    quickReferralModalState.enrolmentSettingsData = {
      start_at_date: djs().format('YYYY-MM-DD'),
      external_id: '',
      enrolment_contract_type: '',
      contract_category: '',
      work_schedule: WorkSchedule.FULL_TIME,
      owned_by: '',
      course_segment: CourseSegment.FULL_COURSE,
      selected_site: {
        site_id: '',
        site_name: '',
        site_state: '',
      },
      selected_consultant: {
        consultant_id: '',
        consultant_name: '',
      },
    };
    quickReferralModalState.existingFields = null;
    quickReferralModalState.existingStudentId = null;
    quickReferralModalState.hasActiveAccreditedCourse = false;
    quickReferralModalState.usiConflict = false;
  },
});
